import { createTheme, MantineColorsTuple } from '@mantine/core';

// Custom color palette for the drawing game
const gamePrimary: MantineColorsTuple = [
  '#e6f9f5',
  '#c2f0e6',
  '#9ae7d7',
  '#6ddcc5',
  '#4cd1b4',
  '#2dc5a1',
  '#0da388',
  '#0a8971',
  '#076f5a',
  '#045644'
];

const gameGreen: MantineColorsTuple = [
  '#ebfbee',
  '#d3f9d8',
  '#b2f2bb',
  '#8ce99a',
  '#69db7c',
  '#51cf66',
  '#40c057',
  '#37b24d',
  '#2f9e44',
  '#2b8a3e'
];

const gameOrange: MantineColorsTuple = [
  '#fff4e6',
  '#ffe8cc',
  '#ffd8a8',
  '#ffc947',
  '#ffb922',
  '#fd9800',
  '#e8890b',
  '#d9770f',
  '#c96508',
  '#b85450'
];

const gamePurple: MantineColorsTuple = [
  '#f3f0ff',
  '#e5dbff',
  '#d0bfff',
  '#b197fc',
  '#9775fa',
  '#845ef7',
  '#7950f2',
  '#7048e8',
  '#6741d9',
  '#5f3dc4'
];

export const theme = createTheme({
  primaryColor: 'gamePrimary',
  
  colors: {
    gamePrimary,
    gameGreen,
    gameOrange,
    gamePurple,
  },

  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
  
  headings: {
    fontFamily: '"Covered By Your Grace", Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
    fontWeight: '600',
    sizes: {
      h1: { fontSize: '2rem', lineHeight: '1.2' },
      h2: { fontSize: '1.5rem', lineHeight: '1.3' },
      h3: { fontSize: '1.25rem', lineHeight: '1.4' },
      h4: { fontSize: '1.125rem', lineHeight: '1.4' },
      h5: { fontSize: '1rem', lineHeight: '1.5' },
      h6: { fontSize: '0.875rem', lineHeight: '1.5' },
    }
  },

  defaultRadius: 'md',

  spacing: {
    xs: '8px',
    sm: '12px', 
    md: '16px',
    lg: '24px',
    xl: '32px',
  },

  breakpoints: {
    xs: '36em',  // 576px
    sm: '48em',  // 768px - tablet
    md: '62em',  // 992px - small desktop
    lg: '75em',  // 1200px - desktop
    xl: '88em',  // 1408px - large desktop
  },

  components: {
    Button: {
      defaultProps: {
        radius: 'md',
      },
      styles: (theme, params) => ({
        root: {
          '@media (hover: none)': {
            // Touch device optimizations
            minHeight: '44px',
            minWidth: '44px',
          },
          // Secondary variant - clear button with green outline
          ...(params.variant === 'outline' && {
            color: '#0da388',
            borderColor: '#0da388',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(13, 163, 136, 0.05)',
            },
          }),
          // Disabled state - grey button with white text
          '&:disabled': {
            backgroundColor: '#b7c7c3 !important',
            color: 'white !important',
            border: 'none !important',
            opacity: '1 !important',
            cursor: 'not-allowed',
            '&:hover': {
              backgroundColor: '#b7c7c3 !important',
            },
          },
          '&[data-disabled]': {
            backgroundColor: '#b7c7c3 !important',
            color: 'white !important',
            border: 'none !important',
            opacity: '1 !important',
            cursor: 'not-allowed',
            '&:hover': {
              backgroundColor: '#b7c7c3 !important',
            },
          },
        },
      }),
    },
    
    Paper: {
      defaultProps: {
        radius: 'md',
        shadow: 'sm',
      },
    },

    Card: {
      defaultProps: {
        radius: 'md',
        shadow: 'sm',
      },
    },

    TextInput: {
      defaultProps: {
        radius: 'md',
        size: 'md',
      },
      styles: {
        input: {
          '@media (max-width: 48em)': {
            minHeight: '44px',
            fontSize: '16px', // Prevents zoom on iOS
          },
        },
      },
    },

    Textarea: {
      defaultProps: {
        radius: 'md',
        size: 'md',
      },
      styles: {
        input: {
          '@media (max-width: 48em)': {
            fontSize: '16px', // Prevents zoom on iOS
          },
        },
      },
    },

    ActionIcon: {
      defaultProps: {
        size: 'lg',
      },
      styles: {
        root: {
          '@media (hover: none)': {
            // Touch device optimizations
            minHeight: '44px',
            minWidth: '44px',
          },
        },
      },
    },
    Avatar: {
      styles: {
        image: {
          backgroundColor: '#ffffff',
        },
      },
    },
  },

  // Custom CSS variables for drawing-specific elements
  other: {
    drawingColors: {
      canvas: '#ffffff',
      canvasBorder: '#e9ecef',
      activeTool: '#0da388',
      cardStack: '#f8f9fa',
      cardActive: '#fff3cd',
      cardComplete: '#d1e7dd',
    },
    backgroundColor: '#e1f2ef',
  },
  
  globalStyles: (theme) => ({
    body: {
      backgroundColor: '#e1f2ef',
    },
    input: {
      fontSize: '16px',
    },
    textarea: {
      fontSize: '16px',
    },
    select: {
      fontSize: '16px',
    },
    '.mantine-Input-input': {
      fontSize: '16px',
    },
  }),
});

export type GameTheme = typeof theme;
